import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Button,
  UncontrolledButtonDropdown,
  Input,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { showModal } from '../actions';
import { clearDroppedFiles } from '../actions/App';
import {
  showUploadModal,
  setUploadModalMode,
  setSelectedFiles,
  setSelectedAllFiles,
} from '../actions/Files';

import UploadModal from './Upload/Modal';
import UploadButtonImporters from './UploadButtonImporters';
import UpgradeButton from './UpgradeButton';
import ImportModal from './Importer/Modal';

import { showIntercomWidget } from '../helpers';
import {
  accountUsageLock,
  subscriptionExpired,
} from '../helpers/App';
import { userCan } from '../helpers/Auth';
import {
  eventSubscribe,
  eventUnsubscribe,
} from '../helpers/Events';
import { removeUnwantedFiles } from '../helpers/Files';
import { getSetting } from '../helpers/Settings';

class UploadButton extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      files: null,
      filesModificationTimestamp: null,
      importModalOpen: false,
      importModalServiceId: null,
    };

    this.fileInput = React.createRef();
    this.folderInput = React.createRef();

    this.triggerInputFile = this.triggerInputFile.bind(this);
    this.triggerInputFolder = this.triggerInputFolder.bind(this);
    this.initUploadFileModal = this.initUploadFileModal.bind(this);
    this.initUploadFolderModal = this.initUploadFolderModal.bind(this);
    this.openImportModal = this.openImportModal.bind(this);
    this.closeImportModal = this.closeImportModal.bind(this);
    this.handleOpenImportModalEvent = this.handleOpenImportModalEvent.bind(this);
  }

  componentDidMount() {
    eventSubscribe('openImportModal', this.handleOpenImportModalEvent);
    eventSubscribe('triggerFileUpload', this.triggerInputFile);
    this.checkImporterModal();
  }

  componentDidUpdate(prevProps) {
    const {
      droppedFiles,
      location,
    } = this.props;
    const { location: prevLocation } = prevProps;

    if (droppedFiles.length > 0 && prevProps.droppedFiles.length === 0) {
      this.initUploadModalFromDrop();
    }

    if (location.search !== prevLocation.search) {
      this.checkImporterModal();
    }
  }

  componentWillUnmount() {
    eventUnsubscribe('openImportModal', this.handleOpenImportModalEvent);
    eventUnsubscribe('triggerFileUpload', this.triggerInputFile);
  }

  checkImporterModal() {
    const {
      profile,
      location,
    } = this.props;

    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('importer') && profile) {
      this.openImportModal(urlParams.get('importer'));
    }
  }

  openImportModal(serviceId) {
    this.setState({
      importModalOpen: true,
      importModalServiceId: serviceId,
    });
  }

  closeImportModal() {
    this.setState({
      importModalOpen: false,
      importModalServiceId: null,
    });
  }

  handleOpenImportModalEvent(event) {
    const {
      location,
      history,
    } = this.props;
    const urlParams = new URLSearchParams(location.search);
    urlParams.append('importer', event.detail);
    history.replace(`${location.pathname}?${urlParams.toString()}`);
    showIntercomWidget();

    this.openImportModal(event.detail);
  }

  triggerInputFile() {
    const { profile } = this.props;

    if (!accountUsageLock(profile.client) && !subscriptionExpired(profile.client)) {
      this.fileInput.current.click();
    }
  }

  triggerInputFolder() {
    const { profile } = this.props;

    if (!accountUsageLock(profile.client) && !subscriptionExpired(profile.client)) {
      this.folderInput.current.click();
    }
  }

  initUploadFileModal(e) {
    const {
      profile,
      openUploadModal,
    } = this.props;
    const { files } = e.target;

    if (accountUsageLock(profile.client) || subscriptionExpired(profile.client)) {
      return;
    }

    if (files.length > 0) {
      this.setState({
        files: [...files],
        filesModificationTimestamp: new Date(),
      }, () => {
        openUploadModal();
        this.fileInput.current.value = null;
      });
    } else {
      this.setState({ files: null });
    }
  }

  initUploadFolderModal(e) {
    const {
      profile,
      openUploadModal,
    } = this.props;
    const { files } = e.target;

    if (accountUsageLock(profile.client) || subscriptionExpired(profile.client)) {
      return;
    }

    if (files.length > 0) {
      const nFiles = Array.from(files);
      nFiles.forEach((file, idx) => {
        if (file.webkitRelativePath) {
          nFiles[idx].hdcFullPath = `/${file.webkitRelativePath}`;
        }
      });

      this.setState({
        files: [...removeUnwantedFiles(nFiles)],
        filesModificationTimestamp: new Date(),
      }, () => {
        openUploadModal();
        this.fileInput.current.value = null;
      });
    } else {
      this.setState({ files: null });
    }
  }

  initUploadModalFromDrop() {
    const {
      profile,
      openUploadModal,
      droppedFiles,
      resetDroppedFiles,
    } = this.props;

    if (accountUsageLock(profile.client) || subscriptionExpired(profile.client)) {
      return;
    }

    if (droppedFiles.length > 0) {
      this.setState({
        files: droppedFiles,
        filesModificationTimestamp: new Date(),
      }, () => {
        resetDroppedFiles();
        openUploadModal();
      });
    } else {
      this.setState({ files: null });
    }
  }

  render() {
    const {
      intl,
      profile,
      openAddLinkModal,
    } = this.props;
    const {
      files,
      filesModificationTimestamp,
      importModalOpen,
      importModalServiceId,
    } = this.state;

    return (
      <>
        {profile && userCan('file.upload') && (
          <div className="upload-btn-wrapper">
            {!getSetting('cs1') && (
              <>
                {' '}
                <Button id="new-link-card" className="link-card-button" color="link" onClick={openAddLinkModal} size="md" aria-label={intl.formatMessage({ defaultMessage: 'Add new Link Card' })}>
                  <i className="far fa-link-horizontal" />
                </Button>
                <UncontrolledTooltip target="new-link-card" fade={false}>
                  {intl.formatMessage({ defaultMessage: 'Add new Link Card' })}
                </UncontrolledTooltip>
              </>
            )}
            <Input
              type="file"
              multiple={true}
              name="file"
              className="upload-file"
              onChange={this.initUploadFileModal}
              innerRef={this.fileInput}
              accept={getSetting('allowed_extensions').map(e => (`.${e}`)).join(',')}
              id="upload-file-input"
              aria-label={intl.formatMessage({ defaultMessage: 'Upload File' })}
            />
            <Input
              type="file"
              multiple={true}
              name="folder"
              className="upload-file"
              onChange={this.initUploadFolderModal}
              innerRef={this.folderInput}
              webkitdirectory="true"
              id="upload-folder-input"
              aria-label={intl.formatMessage({ defaultMessage: 'Upload Folder' })}
            />
            <UncontrolledButtonDropdown group={false} className="upload-button-dropdown">
              <DropdownToggle color="primary" className="dropdown-button" aria-label={intl.formatMessage({ defaultMessage: 'Upload' })}>
                <i className="far fa-upload" />
                <span>{intl.formatMessage({ defaultMessage: 'Upload' })}</span>
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem header>
                  {intl.formatMessage({ defaultMessage: 'Upload' })}
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem
                  onClick={this.triggerInputFile}
                  aria-label={intl.formatMessage({ defaultMessage: 'Upload File' })}
                >
                  <div className="icon-gray">
                    <i className="far fa-file" />
                  </div>
                  {intl.formatMessage({ defaultMessage: 'File' })}
                </DropdownItem>
                <DropdownItem
                  onClick={this.triggerInputFolder}
                  aria-label={intl.formatMessage({ defaultMessage: 'Upload Folder' })}
                >
                  <div className="icon-gray">
                    <i className="far fa-folder" />
                  </div>
                  {intl.formatMessage({ defaultMessage: 'Folder' })}
                </DropdownItem>
                {(profile.importers && profile.importers.length) > 0 && (
                  <>
                    <DropdownItem divider />
                    <UploadButtonImporters
                      openImportModal={this.openImportModal}
                      closeImportModal={this.closeImportModal}
                    />
                  </>
                )}
              </DropdownMenu>
            </UncontrolledButtonDropdown>
            <UpgradeButton />
          </div>
        )}
        {(profile && profile.uuid) && (
          <UploadModal
            files={files}
            filesModificationTimestamp={filesModificationTimestamp}
          />
        )}
        {(profile && profile.uuid) && (
          <ImportModal
            modalOpen={importModalOpen}
            serviceId={importModalServiceId}
            handleClose={this.closeImportModal}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  droppedFiles: state.App.droppedFiles,
});

const mapDispatchToProps = dispatch => ({
  openUploadModal: () => {
    dispatch(setUploadModalMode('upload'));
    dispatch(showUploadModal());
  },
  openAddLinkModal: () => {
    dispatch(setSelectedFiles([]));
    dispatch(setSelectedAllFiles(false));
    dispatch(setUploadModalMode('link'));
    dispatch(showUploadModal());
  },
  openBulkVersioningModal: (collection) => {
    dispatch(showModal('BulkVersioningModal', {
      className: 'modal-bulk-versioning',
      collection,
    }));
  },
  resetDroppedFiles: () => {
    dispatch(clearDroppedFiles());
  },
});

UploadButton.propTypes = {
  intl: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  profile: PropTypes.object,
  droppedFiles: PropTypes.array.isRequired,
  openUploadModal: PropTypes.func.isRequired,
  openAddLinkModal: PropTypes.func.isRequired,
  resetDroppedFiles: PropTypes.func.isRequired,
};

UploadButton.defaultProps = {
  profile: null,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(UploadButton)));
